/**
 * "Tt Normal text ▾" — the block-type picker at the head of the full
 * toolbar (Confluence / Notion shape). One option table drives three
 * surfaces: this dropdown, its compact icon-only stage, and the ⋯ More
 * menu's Text group when the dropdown itself has overflowed.
 *
 * `h1` is deliberately included here even though the wiki doc title
 * renders as h1 outside the body — the card + comment surfaces have no
 * external title, and the option list has shipped this way since Phase 2c.
 *
 * Quote is NOT an option: it is a row button on every preset (it overflows
 * into ⋯ like any other). A blockquote wraps paragraphs, so the caret
 * inside one still reads "Normal text" here — the block STYLE is unchanged.
 *
 * Portalled + viewport-clamped (`anchor-popover.ts`): the old
 * `position: absolute` menu clipped against every nested scrollport.
 */

import type { Editor } from '@tiptap/react'
import type { TFunction } from 'i18next'
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  type LucideIcon,
  Pilcrow,
} from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { resolvePopoverPosition } from '@/lib/anchor-popover'

export type TextStyleOptionKey = 'paragraph' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface TextStyleOption {
  key: TextStyleOptionKey
  /** `common` namespace key suffix under `editor.toolbar.` */
  labelKey:
    | 'normalText'
    | 'heading1'
    | 'heading2'
    | 'heading3'
    | 'heading4'
    | 'heading5'
    | 'heading6'
  isActive: (editor: Editor) => boolean
  run: (editor: Editor) => void
  /** Icon for the ⋯ menu row when the dropdown itself has overflowed. */
  icon: LucideIcon
  /** Tailwind classes for the label's visual size in the dropdown. */
  className: string
  /** Keyboard shortcut hint (TipTap defaults). Display-only. */
  shortcut?: string
}

/**
 * Order + per-item typography mirror Confluence: labels render at roughly
 * the node's actual prose size so the picker is scannable.
 */
export const TEXT_STYLE_OPTIONS: readonly TextStyleOption[] = [
  {
    key: 'paragraph',
    labelKey: 'normalText',
    isActive: (editor) => !editor.isActive('heading'),
    run: (editor) => editor.chain().focus().setParagraph().run(),
    icon: Pilcrow,
    className: 'text-sm',
    shortcut: 'Mod-Alt-0',
  },
  ...([1, 2, 3, 4, 5, 6] as const).map(
    (level): TextStyleOption => ({
      key: `h${level}`,
      labelKey: `heading${level}`,
      isActive: (editor) => editor.isActive('heading', { level }),
      run: (editor) => editor.chain().focus().setHeading({ level }).run(),
      icon: [Heading1, Heading2, Heading3, Heading4, Heading5, Heading6][level - 1] as LucideIcon,
      className: [
        'text-2xl font-bold',
        'text-xl font-bold',
        'text-lg font-semibold',
        'text-base font-semibold',
        'text-sm font-semibold',
        'text-xs font-semibold uppercase tracking-wide',
      ][level - 1] as string,
      shortcut: `Mod-Alt-${level}`,
    }),
  ),
]

export const activeTextStyleOption = (editor: Editor): TextStyleOption =>
  TEXT_STYLE_OPTIONS.find((option) => option.isActive(editor)) ??
  (TEXT_STYLE_OPTIONS[0] as TextStyleOption)

const MENU_WIDTH = 200
const MENU_ROW_HEIGHT = 36

export interface TextStyleDropdownProps {
  editor: Editor
  t: TFunction<'common'>
  /**
   * Icon-only trigger (`Tt ▾`, ~44px) — the layout hook flips this on
   * BEFORE it hides any button, so the widest item gives up its label
   * first. The label moves into the tooltip / aria-label.
   */
  compact?: boolean
  size?: 'md' | 'sm'
  /** Ref on the label span — the overflow hook measures what the compact stage saves. */
  labelRef?: (node: HTMLElement | null) => void
}

export function TextStyleDropdown({
  editor,
  t,
  compact = false,
  size = 'md',
  labelRef,
}: TextStyleDropdownProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(
    null,
  )
  const menuId = useId()

  // Position from the trigger rect with a KNOWN size (see anchor-popover:
  // a fixed box near the viewport edge compresses rather than spills, so
  // measuring a rendered menu would read back a squeezed width).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const anchor = triggerRef.current.getBoundingClientRect()
    setPosition(
      resolvePopoverPosition({
        anchor,
        size: { width: MENU_WIDTH, height: TEXT_STYLE_OPTIONS.length * MENU_ROW_HEIGHT + 8 },
        prefer: 'below',
        align: 'start',
        margin: 6,
      }),
    )
  }, [open])

  // Close on outside pointerdown, Escape (capture — the card modal's
  // dialog handler must not see it), scroll, resize.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
      editor.commands.focus()
    }
    const close = () => setOpen(false)
    // Capture-phase `scroll` also fires for the menu's own overflow list —
    // scrolling the options must not dismiss them.
    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return
      close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open, editor])

  const active = activeTextStyleOption(editor)
  const activeLabel = t(`editor.toolbar.${active.labelKey}`)
  const title = t('editor.toolbar.textStyleCurrent', { style: activeLabel })
  const sizeClasses = size === 'sm' ? 'h-7 px-1.5' : 'h-8 px-2'

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={title}
        title={title}
        className={`inline-flex shrink-0 items-center gap-1 rounded text-sm text-fg transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${sizeClasses}`}
      >
        <span aria-hidden="true" className="font-serif text-[14px]">
          Tt
        </span>
        {!compact && (
          <span ref={labelRef} className="whitespace-nowrap text-xs">
            {activeLabel}
          </span>
        )}
        <span aria-hidden="true" className="text-[10px] text-fg-muted">
          {'▾'}
        </span>
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                width: MENU_WIDTH,
                maxHeight: position.maxHeight,
              }}
              className="z-50 overflow-y-auto rounded-md border border-border bg-bg-elevated py-1 shadow-lg"
            >
              {TEXT_STYLE_OPTIONS.map((option) => {
                const pressed = option.isActive(editor)
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={pressed}
                    onClick={() => {
                      option.run(editor)
                      setOpen(false)
                    }}
                    className={`flex w-full items-center px-3 py-1.5 text-start text-fg hover:bg-bg-muted ${option.className} ${
                      pressed ? 'bg-bg-muted' : ''
                    }`}
                  >
                    {t(`editor.toolbar.${option.labelKey}`)}
                  </button>
                )
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
